import React, { useContext, useEffect, useState } from 'react';
import './JobsTable.scss';
import { AppContext } from 'contexts/AppContext';
import {
  useJobsAndJobsStatuses,
  useSchemasAndTables,
  useGetSignedURL,
} from 'hooks/jobs';
import {
  buildJobsTablePaginatedParams,
  prefetchJobsPaginatedPage,
} from 'utils/jobsQueryCache';
import { JobDTO, seachableTablePageSizeOptions } from 'types/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Modal, notification, Result, Tooltip } from 'antd';
import {
  RedoOutlined,
  ScanOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import { format } from 'date-fns';
import SearchableTable from 'components/reusables/SearchableTable/SearchableTable';
import { ColumnsType, TableProps } from 'antd/es/table';
import TableDateCellRenderer from 'components/JobsTable/TableCellRenderers/DateCellRenderer/DateCellRenderer';
import StatusCellRenderer from 'components/JobsTable/TableCellRenderers/StatusCellRenderer/StatusCellRenderer';
import { prepareDuration } from 'utils/date';
import { JobStatus } from 'components/StatusTag/StatusTag';
import { JobDetailsSidebar } from 'components/JobsTable/JobsDetailsSidebar/JobsDetailsSidebar';
import StatusStatistics from 'components/JobsTable/StatusStatistics/StatusStatistics';
import useSignedURL from 'hooks/useSignedURL';
import useAntdSortColumns from 'hooks/useAntdSortColumns';
import dayjs from 'dayjs';
import useAntdTableChange from 'hooks/useAntdTableChange';
import DurationCellRenderer from './TableCellRenderers/DurationCellRenderer/DurationCellRenderer';
import EfficiencyCellRenderer from './TableCellRenderers/EfficiencyCellRenderer/EfficiencyCellRenderer';
import JobsCompare from './JobsCompare/JobsCompare';
import { AxiosError } from 'axios';

function JobsTable({
  lockedTableFilter,
  lockedSchemaFilter,
}: {
  lockedTableFilter?: string[];
  lockedSchemaFilter?: string[];
} = {}) {
  const { clusterName, currentAppliedFilters, sortBy, sortOrder } =
    useContext(AppContext);
  const isLocked = !!lockedTableFilter;
  const [isShareLoading, setIsShareLoading] = useState(false);
  const intl = useIntl();
  const {
    isSignedURL,
    signature,
    expires,
    prefix,
    schemaFromSignedURL: schemaName,
  } = useSignedURL();
  const isJobsEnabled = !!clusterName;
  const isJobsStatusesEnabled = !!clusterName;
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [jobIDFilter, setJobIDFilter] = useState('');
  const [tableFilter, setTableFilter] = useState<string[]>(
    lockedTableFilter ?? []
  );
  const [schemaFilters, setSchemaFilters] = useState<string[]>(
    lockedSchemaFilter ?? []
  );
  const [dateRangePicker, setDateRangePicker] = useState<
    dayjs.Dayjs[] | null[]
  >([]);
  const [jobStatusFilters, setJobStatusFilters] = useState<JobStatus[]>([]);
  const [limit, setLimit] = useState(seachableTablePageSizeOptions[0]);
  const [page, setPage] = useState(1);

  const shareTableName = tableFilter?.length === 1 ? tableFilter[0] : '';
  const shareSchema = schemaFilters?.length === 1 ? schemaFilters[0] : '';
  const canShare = !!shareTableName && !!shareSchema;

  const { mutateAsync: mutateGetShareURL } = useGetSignedURL({
    clusterName: clusterName as string,
  });

  async function handleShareableLink() {
    if (!canShare) return;
    setIsShareLoading(true);
    try {
      const res = await mutateGetShareURL({
        tableName: shareTableName,
        schema: shareSchema,
        scope: 'table',
      });

      const toBeCopiedURL = res.data.signedURL;

      Modal.info({
        title: intl.formatMessage({ id: 'actions.signedURLModal.title' }),
        content: `Shareable link for table ${shareTableName} in schema ${shareSchema}`,
        centered: true,
        width: 620,
        okText: intl.formatMessage({ id: 'actions.signedURLModal.okText' }),
        closable: true,
        maskClosable: true,
        async onOk() {
          await navigator.clipboard.writeText(toBeCopiedURL);
          notification.success({
            message: intl.formatMessage({
              id: 'actions.signedURLModal.successMessage',
            }),
          });
        },
      });
    } catch (error: unknown) {
      const err = error as AxiosError;
      notification.error({ message: err.message });
    } finally {
      setIsShareLoading(false);
    }
  }

  useEffect(() => {
    setPage(1);
  }, [currentAppliedFilters]);

  const tableFilterActive = tableFilter?.length > 0;
  const [selectedJobs, setSelectedJobs] = useState<JobDTO[]>([]);
  const [compareVisible, setCompareVisible] = useState(false);

  const rowSelection: TableProps<JobDTO>['rowSelection'] = {
    selectedRowKeys: selectedJobs.map((job) => job.sparkJobId),
    onChange: (selectedRowKeys: React.Key[], selectedRows: JobDTO[]) => {
      setSelectedJobs(selectedRows);
    },
    getCheckboxProps: (record: JobDTO) => ({
      disabled: selectedJobs.length === 2 && !selectedJobs.includes(record),
    }),
    hideSelectAll: true,
  };

  const handleCompareTwoJobs = () => {
    setCompareVisible(!compareVisible);
  };

  useEffect(() => {
    setSelectedJobs([]);
  }, [
    tableFilter,
    schemaFilters,
    dateRangePicker,
    jobIDFilter,
    jobStatusFilters,
  ]);

  const [
    { refetch: refetchJobsStatus },
    {
      data: jobsData,
      isLoading: isJobsLoading,
      error: jobsError,
      refetch: refetchJobs,
      isFetching: isJobsFetching,
      dataUpdatedAt: jobsUpdatedAt,
    },
  ] = useJobsAndJobsStatuses({
    clusterName: clusterName as string,
    isJobsEnabled,
    isJobsStatusesEnabled,
    pageSize: limit,
    pageNumber: page,
    isSignedURL,
    expires: isSignedURL && expires ? expires : undefined,
    signature: isSignedURL && signature ? signature : undefined,
    prefix: isSignedURL && prefix ? prefix : undefined,
    tableName:
      isSignedURL && lockedTableFilter ? lockedTableFilter[0] : tableFilter,
    schemaName:
      isSignedURL && lockedSchemaFilter ? lockedSchemaFilter[0] : schemaFilters,
    ...(Object.keys(currentAppliedFilters)?.length
      ? { filters: currentAppliedFilters }
      : {}),
    status: jobStatusFilters,
    sortBy,
    sortOrder,
    start_date: dateRangePicker?.[0]?.toISOString(),
    end_date: dateRangePicker?.[1]?.toISOString(),
  });

  async function refetchJobsAndStatuses() {
    refetchJobs();
    refetchJobsStatus();
  }

  const { getSortOptions } = useAntdSortColumns();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const jobs = jobsData?.dtos || [];
  const current = jobsData?.currentPage;
  const total = jobsData?.numberOfJobs;
  const loading = isJobsLoading || isJobsFetching;

  useEffect(() => {
    if (!clusterName || loading || !jobsData) return;

    const nextPage = page + 1;
    const hasNextPage = page * limit < (total ?? 0);
    if (!hasNextPage) return;

    const nextPageParams = buildJobsTablePaginatedParams({
      clusterName,
      pageNumber: nextPage,
      pageSize: limit,
      tableFilter,
      schemaFilters,
      jobStatusFilters,
      sortBy,
      sortOrder,
      dateRangePicker,
      isSignedURL,
      expires: isSignedURL && expires ? expires : undefined,
      signature: isSignedURL && signature ? signature : undefined,
      prefix: isSignedURL && prefix ? prefix : undefined,
      lockedTableFilter,
      lockedSchemaFilter,
    });

    void prefetchJobsPaginatedPage(nextPageParams).catch(() => {});
  }, [
    clusterName,
    page,
    loading,
    jobsData,
    total,
    limit,
    tableFilter,
    schemaFilters,
    jobStatusFilters,
    sortBy,
    sortOrder,
    dateRangePicker,
    isSignedURL,
    expires,
    signature,
    prefix,
    lockedTableFilter,
    lockedSchemaFilter,
  ]);

  const {
    data: schemasAndTablesData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isError: hasSchemasAndTablesError,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isLoading: isSchemasAndTablesLoading,
  } = useSchemasAndTables();

  const [durations, setDurations] = useState(jobs.map(() => 0));

  useEffect(() => {
    const intervals = jobs.map((job, index) => {
      function doSet() {
        const currentTime = new Date().getTime();
        const createdAtTime = new Date(job.createdAt).getTime();
        const duration = currentTime - createdAtTime;
        setDurations((prevDurations) => {
          const newDurations = [...prevDurations];
          newDurations[index] = duration;
          return newDurations;
        });
      }
      doSet();
      const intervalId = setInterval(doSet, 3000);
      return () => clearInterval(intervalId);
    });
    return () => intervals.forEach((doClear) => doClear());
  }, [jobs]);

  const columns: ColumnsType<JobDTO> = [
    {
      title: <FormattedMessage id="columns.job" />,
      dataIndex: 'sparkJobId',
      key: 'sparkJobId',
      width: '180px',
      render(value, { sparkJobId }) {
        return (
          <Tooltip title={sparkJobId} placement="topLeft">
            <div className="jobs-table__job-id">
              <Button type="link" onClick={() => setSelectedJobId(sparkJobId)}>
                <div className="jobs-table__job-id-value">{sparkJobId}</div>
              </Button>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: <FormattedMessage id="columns.status" />,
      key: 'sparkJobStatus',
      dataIndex: 'sparkJobStatus',
      width: '100px',
      render(value, { sparkJobStatus }) {
        return <StatusCellRenderer status={sparkJobStatus} />;
      },
    },
    {
      title: () => (
        <div className="jobs-table__creation-time-column-header">
          <FormattedMessage id="columns.creationTime" />
        </div>
      ),
      key: 'CreatedAt',
      dataIndex: 'CreatedAt',
      width: '120px',
      render(value, { createdAt }) {
        return <TableDateCellRenderer originalValue={createdAt.toString()} />;
      },
      ...getSortOptions({ fieldName: 'CreatedAt' }),
    },
    {
      title: <FormattedMessage id="columns.duration" />,
      dataIndex: 'duration',
      key: 'duration',
      width: '160px',
      render(valueInMSec, { sparkJobStatus }, index) {
        let durationMSec;
        if (
          sparkJobStatus === JobStatus.Running ||
          sparkJobStatus === JobStatus.In_Queue
        ) {
          durationMSec = durations[index];
        } else {
          durationMSec = valueInMSec;
        }
        const duration = prepareDuration(durationMSec);
        return <DurationCellRenderer duration={duration} />;
      },
    },
    {
      title: (
        <Tooltip
          title={intl.formatMessage({ id: 'efficiency.headerTooltip' })}
          placement="top">
          <span>
            <FormattedMessage id="columns.efficiency" />
          </span>
        </Tooltip>
      ),
      dataIndex: 'efficiency',
      key: 'efficiency',
      width: '120px',
      render(_value, { efficiency }) {
        return <EfficiencyCellRenderer value={efficiency} />;
      },
    },
    {
      title: <FormattedMessage id="columns.schema" />,
      dataIndex: 'schemaName',
      key: 'schemaName',
      width: '170px',
      render(value) {
        return (
          <span className="jobs-table__schema-name-cell">
            <article className="jobs-table__schema-name-cell-value">
              {value}
            </article>
          </span>
        );
      },
    },
    {
      title: <FormattedMessage id="columns.tableName" />,
      dataIndex: 'tableName',
      key: 'tableName',
      width: '170px',
      render(value) {
        return (
          <Tooltip
            className="new-feature-tooltip-chidori"
            title={
              <div className="new-feature-tooltip-chidori__text">
                <span>We moved things around!</span>
                <span>
                  Please use the tables' filter to access the jobs compare
                  feature
                </span>
              </div>
            }>
            <span className="jobs-table__table-name-cell">{value}</span>
          </Tooltip>
        );
      },
    },
  ];

  const { onChange } = useAntdTableChange();

  if (jobsError) {
    return (
      <Result
        status="error"
        title={<FormattedMessage id="somethingWentWrong" />}
        extra={[
          <Button type="primary" onClick={() => refetchJobsAndStatuses()}>
            Retry
          </Button>,
        ]}
      />
    );
  }

  return (
    <div className="jobs-table__wrapper">
      {!isSignedURL && (
        <StatusStatistics
          jobStatusFilters={jobStatusFilters}
          setJobStatusFilters={setJobStatusFilters}
        />
      )}
      <section className="jobs-table__title">
        <FormattedMessage id="table.title" />
        <div className="jobs-table__title__right">
          <section className="jobs-table__title-sync">
            <label>
              <FormattedMessage
                id="table.lastSync"
                values={{
                  time: jobsUpdatedAt
                    ? format(new Date(jobsUpdatedAt), 'hh:mm:ss a')
                    : '',
                }}
              />
            </label>
            <Tooltip title={<FormattedMessage id="table.refreshTitle" />}>
              <Button
                icon={<RedoOutlined rotate={-90} />}
                className="jobs-table__title-sync-btn"
                onClick={() => refetchJobsAndStatuses()}
                loading={isJobsFetching}
              />
            </Tooltip>
          </section>
          <section className="jobs-table__generate-shareable-table">
            <Tooltip
              title={
                canShare
                  ? 'Click to generate a shareable link'
                  : 'Select exactly one table and one schema to generate a shareable link'
              }>
              <Button
                className="shareable-link-button"
                onClick={handleShareableLink}
                loading={isShareLoading}
                disabled={!canShare}>
                <ShareAltOutlined />
              </Button>
            </Tooltip>
          </section>
        </div>
      </section>
      <SearchableTable<JobDTO>
        showAllJobs={true}
        isLoading={loading}
        dataSource={jobs}
        dataSourceTotalCount={total ?? 0}
        columns={columns}
        loading={isJobsLoading}
        rowSelection={tableFilterActive ? rowSelection : undefined}
        addPostContent={tableFilterActive}
        PostContent={
          tableFilterActive ? (
            <section className="jobs-table-details__post-content">
              <Button
                type="default"
                size="middle"
                onClick={handleCompareTwoJobs}
                disabled={selectedJobs.length !== 2}>
                <Tooltip title={intl.formatMessage({ id: 'job.compare' })}>
                  <ScanOutlined />
                </Tooltip>
              </Button>
            </section>
          ) : undefined
        }
        pagination={{
          pageSize: limit,
          current,
          total,
          showSizeChanger: true,
          pageSizeOptions: seachableTablePageSizeOptions,
          onShowSizeChange: (_, size) => {
            setLimit(size);
          },
          onChange(newPage) {
            setPage(newPage);
          },
          simple: { readOnly: true },
        }}
        rowKey={'sparkJobId'}
        onChange={onChange}
        jobIDFilter={jobIDFilter}
        setJobIDFilter={setJobIDFilter}
        jobStatusFilters={jobStatusFilters}
        setJobStatusFilters={setJobStatusFilters}
        dateRangePicker={dateRangePicker}
        setDateRangePicker={setDateRangePicker}
        tableFilter={tableFilter}
        setTableFilter={isLocked ? () => { } : setTableFilter}
        schemaFilters={schemaFilters}
        setSchemaFilters={isLocked ? () => { } : setSchemaFilters}
        disableFilterSelectors={isLocked}
        schemas={schemasAndTablesData?.Schemas || []}
        tables={schemasAndTablesData?.Tables || []}
        scroll={{ x: 1132 }}
      />
      <Modal
        open={compareVisible}
        onCancel={handleCompareTwoJobs}
        footer={null}
        width={1550}>
        <JobsCompare jobs={selectedJobs} />
      </Modal>
      <JobDetailsSidebar
        jobId={selectedJobId}
        clusterName={clusterName as string}
        onClose={() => setSelectedJobId(null)}
      />
    </div>
  );
}

export default JobsTable;
